import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faLayerGroup,
} from '@fortawesome/pro-regular-svg-icons';
import { Text } from '@querodelivery/uni-react';
import {
  MAP_ACTION_LEGEND_LABEL,
  MAP_LEGEND_ITEMS,
  MAP_LINE_LEGEND_LABEL,
  MAP_MANAGER_ACTION_LEGEND_LABEL,
} from './constants';
import type { MapLegendTarget } from './types';
import styles from './map-legend-chips.module.scss';

interface MapLegendChipsParams {
  focusedTarget: string;
  availableTargets: MapLegendTarget[];
  hasRoute: boolean;
  hasActions: boolean;
  hasManagerActions?: boolean;
  onFocus: (target: MapLegendTarget) => void;
}

export const MapLegendChips = ({
  focusedTarget,
  availableTargets,
  hasRoute,
  hasActions,
  hasManagerActions = false,
  onFocus,
}: MapLegendChipsParams) => {
  const [isOpen, setIsOpen] = useState(false);
  const items = MAP_LEGEND_ITEMS.filter(item =>
    availableTargets.includes(item.id),
  );
  const hasContent =
    hasRoute || hasActions || hasManagerActions || items.length > 0;

  if (!hasContent) {
    return null;
  }

  return (
    <div className={styles.overlay}>
      {isOpen && (
        <div className={styles.panel}>
          <ul className={styles.list}>
            {hasRoute && (
              <li>
                <span className={styles.item}>
                  <span className={styles.symbol} aria-hidden>
                    <span className={styles.line} />
                  </span>
                  <Text
                    as="span"
                    fontSize="xs"
                    fontWeight="medium"
                    tone="secondary"
                  >
                    {MAP_LINE_LEGEND_LABEL}
                  </Text>
                </span>
              </li>
            )}
            {hasActions && (
              <li>
                <span className={styles.item}>
                  <span className={styles.symbol} aria-hidden>
                    <span className={`${styles.pin} ${styles.action}`} />
                  </span>
                  <Text
                    as="span"
                    fontSize="xs"
                    fontWeight="medium"
                    tone="secondary"
                  >
                    {MAP_ACTION_LEGEND_LABEL}
                  </Text>
                </span>
              </li>
            )}
            {hasManagerActions && (
              <li>
                <span className={styles.item}>
                  <span className={styles.symbol} aria-hidden>
                    <span
                      className={`${styles.pin} ${styles.action} ${styles.executedByManager}`}
                    />
                  </span>
                  <Text
                    as="span"
                    fontSize="xs"
                    fontWeight="medium"
                    tone="secondary"
                  >
                    {MAP_MANAGER_ACTION_LEGEND_LABEL}
                  </Text>
                </span>
              </li>
            )}
            {items.map(item => {
              const isFocused = focusedTarget === item.id;

              return (
                <li key={item.id}>
                  <button
                    type="button"
                    className={`${styles.item} ${
                      isFocused ? styles.itemFocused : ''
                    }`}
                    aria-pressed={isFocused}
                    onClick={() => onFocus(item.id)}
                  >
                    <span className={styles.symbol} aria-hidden>
                      <span className={`${styles.pin} ${styles[item.id]}`} />
                    </span>
                    <Text
                      as="span"
                      fontSize="xs"
                      fontWeight="medium"
                      tone={isFocused ? 'highlight' : 'secondary'}
                    >
                      {item.label}
                    </Text>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
      <button
        type="button"
        className={`${styles.toggle} ${isOpen ? styles.toggleOpen : ''}`}
        aria-expanded={isOpen}
        onClick={() => setIsOpen(current => !current)}
      >
        <FontAwesomeIcon icon={faLayerGroup} className={styles.icon} />
        <Text as="span" fontSize="xs" fontWeight="semibold" tone="secondary">
          Legenda
        </Text>
        <FontAwesomeIcon
          icon={faChevronDown}
          className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ''}`}
        />
      </button>
    </div>
  );
};
